import React, { forwardRef, useImperativeHandle } from "react";
import { ComponentI } from "../../../../types";
import Carousel from "../../../../componentLibrary/Carousel";
import { parsePreviousAnswers } from "../../utils/parsePreviousAnswers";
import { renderContent } from "../../utils/renderComponent";
import ForEachContainer from "../ForEachContainer";

type Props = {
  component: ComponentI;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allResponses: Record<string, any>;
  onChange: (
    variable: string,
    label: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    displayAs?: string,
  ) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalProps: Record<string, any>;
  pageIndex: number;
  inputType: "input" | "textarea";
};

export interface ForEachComponentRef {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  syncVariableChange: (variable: string, answers: Record<string, any>) => void;
}

const ForEachComponent = forwardRef<ForEachComponentRef, Props>(
  (
    {
      component,
      allResponses,
      onChange,
      additionalProps,
      pageIndex,
      inputType = "input",
    },
    ref,
  ) => {
    const [linkedAnswers, setLinkedAnswers] = React.useState<string[]>([]);
    const [answers, setAnswers] = React.useState<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Map<string, Record<string, any>>
    >(new Map());

    const [maxAnswers, setMaxAnswers] = React.useState<number>(0);

    const removeExtraAnswers = (newLinkedAnswers: string[]) => {
      const maxOldIndex = maxAnswers - 1;
      const maxNewIndex = newLinkedAnswers.length - 1;

      const allVariableNames = component.content.componentsList
        ?.map(
          (componentFromList: ComponentI) => componentFromList.content.variable,
        )
        ?.filter((variable: string) => variable !== undefined);

      if (maxOldIndex > maxNewIndex) {
        // Clear variables for indices that are no longer needed
        for (let i = maxNewIndex + 1; i <= maxOldIndex; i++) {
          allVariableNames?.forEach((variableName) => {
            const indexedVariableName = `${variableName}_${i}`;
            // Clear the variable by setting it to undefined or empty
            onChange(indexedVariableName, "", undefined);
          });
        }
      }
    };

    // Expose function to parent component
    useImperativeHandle(ref, () => ({
      syncVariableChange: (
        variableName: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        answers: Record<string, any>,
      ) => {
        if (component.content.linkedVariable?.includes(variableName)) {
          const linkedOptions = parseForEachChoices(answers);

          if (linkedOptions) {
            setLinkedAnswers(linkedOptions);
          }
        }
      },
    }));
    const handleChange = (
      variable: string,
      label: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any,
      linkedAnswer: string,
      displayAs?: string,
    ) => {
      setAnswers((prevAnswers) => {
        const newAnswers = new Map(prevAnswers);
        const existingData = newAnswers.get(linkedAnswer) || {};
        newAnswers.set(linkedAnswer, {
          ...existingData,
          [variable]: { label, value, displayAs },
        });
        return newAnswers;
      });
    };

    const saveState = (latestAnswer, linkedOptions) => {
      // Save answers for each linkedOption
      if (linkedOptions) {
        linkedOptions.forEach((linkedOption, index) => {
          const newAllResponses = {
            ...allResponses,
            [component.content.parsingVariable]: { value: linkedOption },
          };
          // Save each variable for this linkedOption
          Object.keys(latestAnswer.get(linkedOption) || {}).forEach(
            (variableName) => {
              const answerData = latestAnswer.get(linkedOption)?.[variableName];
              if (answerData) {
                const indexedVariableName = `${variableName}_${index}`;
                onChange(
                  indexedVariableName,
                  parsePreviousAnswers(answerData.label, newAllResponses),
                  answerData.value,
                  answerData.displayAs,
                );
              }
            },
          );
        });
        removeExtraAnswers(linkedOptions);
      }
    };

    React.useEffect(() => {
      saveState(answers, linkedAnswers);
    }, [answers]);

    React.useEffect(() => {
      // Below Logic Working well
      setAnswers((prevAnswers) => {
        const newAnswers = new Map();

        // Find unmapped answers (answers that don't have a corresponding linkedAnswer)
        const unmappedAnswers = new Map();
        prevAnswers.forEach((answerData, oldLinkedAnswer) => {
          if (!linkedAnswers.includes(oldLinkedAnswer)) {
            unmappedAnswers.set(oldLinkedAnswer, answerData);
          }
        });

        // Find linkedAnswers that don't have mapped data
        const unmappedLinkedAnswers = linkedAnswers.filter(
          (linkedAnswer) => !prevAnswers.has(linkedAnswer),
        );

        // Map existing answers to their corresponding linkedAnswers
        linkedAnswers.forEach((linkedAnswer) => {
          if (prevAnswers.has(linkedAnswer)) {
            // Keep existing mapping
            newAnswers.set(linkedAnswer, prevAnswers.get(linkedAnswer)!);
          } else if (
            unmappedAnswers.size > 0 &&
            unmappedLinkedAnswers.includes(linkedAnswer)
          ) {
            // Assign unmapped answer to this linkedAnswer (handles updates)
            const [oldLinkedAnswer, answerData] = unmappedAnswers
              .entries()
              .next().value;
            unmappedAnswers.delete(oldLinkedAnswer);
            newAnswers.set(linkedAnswer, answerData);
          }
        });

        return newAnswers;
      });
      setMaxAnswers((prev) => Math.max(prev, linkedAnswers.length));
    }, [linkedAnswers]);

    const loadState = () => {
      const linkedOptions = parseForEachChoices(allResponses);
      // Load answers for each linkedOption
      if (linkedOptions) {
        linkedOptions.forEach((linkedOption, index) => {
          // Check each variable in componentsList for variable_index
          component.content.componentsList?.forEach(
            (componentFromList: ComponentI) => {
              const variableName = componentFromList.content.variable;
              if (variableName) {
                const indexedVariableName = `${variableName}_${index}`;
                const existingResponse = allResponses[indexedVariableName];

                if (existingResponse) {
                  // Map the existing response to the linkedOption at the same index
                  handleChange(
                    variableName,
                    existingResponse.label || componentFromList.content.label,
                    existingResponse.value,
                    linkedOption,
                    existingResponse.displayAs,
                  );
                }
              }
            },
          );
        });
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parseForEachChoices = (allResponses1: Record<string, any>) => {
      const linkedOptions = component.content.linkedVariable?.flatMap(
        (variable: string) => {
          const response = allResponses1[variable];
          if (!response?.value) return [];
          return Array.isArray(response.value)
            ? response.value
            : [response.value];
        },
      );
      return linkedOptions;
    };

    const renderedComponents = linkedAnswers.flatMap((linkedAnswer, index) => (
      <ForEachContainer
        key={`${pageIndex}_foreach_${linkedAnswer}`}
        linkedAnswer={linkedAnswer}
      >
        {component.content.componentsList?.map(
          (componentFromList: ComponentI, index1: number) => {
            const answerData = answers.get(linkedAnswer) || {};
            const result = renderContent({
              component: componentFromList,
              index,
              pageIndex,
              value: answerData,
              allResponses: {
                ...answerData,
                [component.content.parsingVariable]: { value: linkedAnswer },
              },
              onChange: (
                variable: string,
                label: string,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                value: any,
                displayAs?: string,
              ) =>
                handleChange(variable, label, value, linkedAnswer, displayAs),
              additionalProps,
              inputType,
              key: `${pageIndex}_foreach_${componentFromList.type}_${linkedAnswer}_${index}_${index1}`,
            });
            return result;
          },
        )}
      </ForEachContainer>
    ));

    React.useEffect(() => {
      const intiialOptions = parseForEachChoices(allResponses);
      if (intiialOptions) {
        setLinkedAnswers(intiialOptions);
      }
      loadState();
    }, []);

    return (
      <div>
        <Carousel showButtonsUp>{renderedComponents}</Carousel>
      </div>
    );
  },
);

ForEachComponent.displayName = "ForEachComponent";

export default ForEachComponent;
